import { useUpdateEffect } from '@react-hookz/web';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useMemo } from 'react';
import { GetDataResponse } from '@/plugin-system/model';
import { DataQueriesProvider } from './DataQueriesProvider';
import { DataQueriesProviderProps } from './model';

const EMPTY_DATA_QUERIES: DataQueriesProviderProps['queries'] = [];
/**
 * Simple `DataQueriesProvider` that sets the queries to an empty array. Useful when working with features in viz
 * framework that rely on the presence of a `DataQueriesProvider` when you do not need data (e.g. when working with
 * the Markdown panel).
 */
export const EmptyDataQueriesProvider = ({ children }: Pick<DataQueriesProviderProps, 'children'>) => {
  return <DataQueriesProvider queries={EMPTY_DATA_QUERIES}>{children}</DataQueriesProvider>;
};

export interface StaticDataQueriesProviderProps extends Pick<DataQueriesProviderProps, 'children'> {
  /**
   * Data to be provided by the data queries provider.
   *
   * Make sure the data is memoized to avoid unnecessary work being done to update the data on every render cycle.
   */
  data: GetDataResponse;
}

/**
 * Simple wrapper that takes a `GetDataResponse` and sets up a simple `DataQueriesProvider` with a query function that
 * returns that data. Not intended for handling asynchronous data fetching, which is handled by the custom query
 * function option on the `DataQueriesProvider`.
 *
 * Note that features that use this are unlikely to be compatible with functionality like "Add to dashboard" and "Add to
 * notebook." If you want to support those features, instead use a standard `DataQueriesProvider` with a data query
 * plugin.
 */
export const StaticDataQueriesProvider = ({ data, children }: StaticDataQueriesProviderProps) => {
  // Unique identifier for this custom data.
  const id = useId();
  // Memoized, so that this doesn't trigger the useEffect below on every render cycle.
  const queryKey: QueryKey = useMemo(() => {
    return ['static_data_query', id];
  }, [id]);

  // Very simple query function that just returns the data as is. Used to initialize the query.
  const queryFn = useCallback(() => {
    return data;
  }, [data]);

  const queryClient = useQueryClient();
  // `useUpdateEffect` ignores the first render, which is what we want here because the data will be loaded the "normal"
  // way using the `queryFn` on the first render. This will just handle `data` updates after that.
  useUpdateEffect(() => {
    // Whenever the data changes, update the data in react-query. Doing this directly with the queryClient API because
    // it's much easier than trying to put an arbitrary value change in the query key to trigger re-calling the queryFn.
    // Not handling this by putting `data` in the query key because query data is often large and complex and the
    // query key comparison/invalidation in react-query could have performance issues if we did that.
    queryClient.setQueryData(queryKey, () => {
      return data;
    });

    // Note: queryClient and queryKey are generally stable, so this should primarily trigger on `data` changes.
  }, [data, queryClient, queryKey]);

  return <DataQueriesProvider queries={[{ queryKey: queryKey, queryFn }]}>{children}</DataQueriesProvider>;
};
